import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export interface Customer {
  id: string;
  customerType: 'Individual' | 'Organization';
  customerName: string;
  idNumber: string;
  mobileNo1: string;
  mobileNo2?: string;
  /** Email is optional system-wide (#18). Null/empty for customers without an address (incl. walk-ins). */
  email: string | null;
  county: string;
  town: string;
  road: string;
  physicalLocationName: string;
  gpsLocation: string;
  customerGroupId: string;
  creditLimit: number;
  paymentTerms: string;
  isActive: boolean;
  /** True when first created via the walk-in fast-path (#24). Immutable after creation. */
  isWalkIn?: boolean;
  /** GM-set override for the minimum deposit % before sowing can begin. Null = system default. */
  sowingPaymentThresholdOverride?: number | null;
  createdAt: string;
}

export interface CreateCustomerRequest {
  customerType: 'Individual' | 'Organization';
  customerName: string;
  idNumber: string;
  mobileNo1: string;
  mobileNo2: string;
  /** Optional system-wide (#18). Send empty string or omit when not provided. */
  email: string;
  county: string;
  town: string;
  road: string;
  physicalLocationName: string;
  gpsLocation: string;
  customerGroupId: string | null;
  creditLimit: number;
  paymentTerms: string;
}

export interface UpdateCustomerRequest extends CreateCustomerRequest {
  isActive: boolean;
}

export interface ImportError {
  rowNumber: number;
  customerName: string;
  email: string | null;
  errors: string[];
}

export interface ImportResult {
  totalRows: number;
  successCount: number;
  failureCount: number;
  errors: ImportError[];
  importedCustomers: Customer[];
}

export interface ImportJobStatus {
  jobId: string;
  entityType: string;
  fileName: string;
  status: 'Pending' | 'Processing' | 'Completed' | 'Failed';
  totalRows: number;
  successCount: number;
  failureCount: number;
  errors: ImportError[] | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export type CustomerSortBy =
  | 'customerName'
  | 'email'
  | 'town'
  | 'creditLimit'
  | 'createdAt';

export interface CustomerListQuery {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sortBy?: CustomerSortBy;
  sortDirection?: 'asc' | 'desc';
  isActive?: boolean;
  customerGroupId?: string;
  /** Region (county) filter, e.g. "Nairobi" — matched case-insensitively. */
  county?: string;
}

export interface CustomerListResult {
  items: Customer[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export const customersApi = {
  getCustomers: async (query: CustomerListQuery = {}): Promise<CustomerListResult> => {
    const params = new URLSearchParams();
    if (query.pageNumber != null) params.set('pageNumber', String(query.pageNumber));
    if (query.pageSize != null) params.set('pageSize', String(query.pageSize));
    if (query.search) params.set('search', query.search);
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDirection) params.set('sortDirection', query.sortDirection);
    if (query.isActive != null) params.set('isActive', String(query.isActive));
    if (query.customerGroupId) params.set('customerGroupId', query.customerGroupId);
    if (query.county) params.set('county', query.county);
    const qs = params.toString();
    const { data } = await apiClient.get<ApiResponse<CustomerListResult>>(
      qs ? `/Customers?${qs}` : '/Customers'
    );
    return data.data;
  },

  /**
   * DB-side count of customers matching a criteria matrix (region/county, town,
   * customer type, active status). Used by the call-center "create customer
   * group by" dialog to preview how many customers a filter will pull in.
   * At least one criterion must be supplied — the backend rejects blanks.
   */
  getCustomersCount: async (
    criteria: Pick<CustomerListQuery, 'county'> & {
      town?: string;
      customerType?: string;
      isActive?: boolean;
    }
  ): Promise<number> => {
    const params = new URLSearchParams();
    if (criteria.county) params.set('county', criteria.county);
    if (criteria.town) params.set('town', criteria.town);
    if (criteria.customerType) params.set('customerType', criteria.customerType);
    if (criteria.isActive != null) params.set('isActive', String(criteria.isActive));
    const { data } = await apiClient.get<ApiResponse<{ totalCount: number }>>(
      `/Customers/count?${params.toString()}`
    );
    return data.data.totalCount;
  },

  getCustomerById: async (id: string): Promise<Customer> => {
    const { data } = await apiClient.get<ApiResponse<Customer>>(
      `/Customers/${id}`
    );
    return data.data;
  },

  createCustomer: async (payload: CreateCustomerRequest): Promise<Customer> => {
    const { data } = await apiClient.post<ApiResponse<Customer>>(
      '/Customers',
      payload
    );
    return data.data;
  },

  updateCustomer: async (
    id: string,
    payload: UpdateCustomerRequest
  ): Promise<Customer> => {
    const { data } = await apiClient.put<ApiResponse<Customer>>(
      `/Customers/${id}`,
      payload
    );
    return data.data;
  },

  deleteCustomer: async (id: string): Promise<void> => {
    await apiClient.delete(`/Customers/${id}`);
  },

  getCustomerGroups: async (): Promise<string[]> => {
    const { data } = await apiClient.get<ApiResponse<string[]>>(
      '/CustomerGroups'
    );
    return data.data;
  },

  downloadImportTemplate: async (): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(
      '/Customers/import/template',
      {
        responseType: 'blob'
      }
    );
    return data;
  },

  importCustomers: async (file: File): Promise<ImportJobStatus> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<ApiResponse<ImportJobStatus>>(
      '/Customers/import',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return data.data;
  },

  getImportJobStatus: async (jobId: string): Promise<ImportJobStatus> => {
    const { data } = await apiClient.get<ApiResponse<ImportJobStatus>>(
      `/Customers/import/${jobId}`
    );
    return data.data;
  },

  /**
   * Downloads the filtered customer list as CSV (same filters as the list).
   * Backend streams DB-side batches — safe for large lists.
   */
  exportCustomers: async (
    query: Omit<CustomerListQuery, 'pageNumber' | 'pageSize'> & { maxRows?: number }
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    if (query.search) params.set('search', query.search);
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDirection) params.set('sortDirection', query.sortDirection);
    if (query.isActive != null) params.set('isActive', String(query.isActive));
    if (query.customerGroupId) params.set('customerGroupId', query.customerGroupId);
    if (query.county) params.set('county', query.county);
    if (query.maxRows != null) params.set('maxRows', String(query.maxRows));
    const qs = params.toString();
    const { data } = await apiClient.get<Blob>(
      qs ? `/Customers/export?${qs}` : '/Customers/export',
      { responseType: 'blob' }
    );
    return data;
  },

  setSowingPaymentThreshold: async (
    customerId: string,
    thresholdPercentage: number | null
  ): Promise<void> => {
    await apiClient.patch(`/Customers/${customerId}/sowing-payment-threshold`, {
      thresholdPercentage,
    });
  },
};
